// Iter56 — /stock-locks workspace page.
//
// Two sections:
//   §1 Active Locks (requested + locked) — filters by status
//   §2 Released / Cancelled (history)
// Header: "+ New Stock Lock" → modal
//
// Locks are tenant-scoped action-execution requests. Workflow:
//   responder requests → warehouse worker locks (records locations/qty/type)
//   → authorized user releases with a note.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { NewStockLockModal } from "../components/NewStockLockModal";
import { LockActionPanel } from "../components/LockActionPanel";
import { UniversalFilter } from "../components/filters/UniversalFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  requested: { bg: "rgba(243,177,74,0.16)", fg: "#f3b14a", label: "REQUESTED" },
  locked: { bg: "rgba(226,75,74,0.16)", fg: "#ff8a89", label: "LOCKED" },
  released: { bg: "rgba(78,208,132,0.16)", fg: "#9aebc2", label: "RELEASED" },
  cancelled: { bg: "rgba(120,125,140,0.16)", fg: "#9aa1b3", label: "CANCELLED" },
};

const LOCK_TYPE_COLOR = {
  quarantine: "#f3b14a",
  blocked: "#E24B4A",
  hold: "#9ec3ec",
};

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

export default function StockLocks() {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);

  const canAct = hasModuleAccess(user, "stock_lock") &&
    ["responder", "escalation_manager", "tenant_admin", "platform_admin"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, requested: 0, locked: 0, released: 0, cancelled: 0 });
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [actionLock, setActionLock] = useState(null);
  const [focusId, setFocusId] = useState(null);

  // Read ?focus= deep-link
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const f = params.get("focus");
      if (f) setFocusId(f);
      if (params.get("new") === "1") setNewOpen(true);
    } catch (_) { /* noop */ }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/stock-locks?limit=300`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      addToast({ type: "error", title: "Failed to load stock locks", body: err.message });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { refresh(); }, [refresh]);

  // Universal filter chips
  const chips = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: [
      { value: "requested", label: "Requested", predicate: (i) => i.status === "requested" },
      { value: "locked", label: "Locked", predicate: (i) => i.status === "locked" },
      { value: "released", label: "Released", predicate: (i) => i.status === "released" },
      { value: "cancelled", label: "Cancelled", predicate: (i) => i.status === "cancelled" },
    ]},
    { key: "link", label: "Link", type: "select", options: [
      { value: "linked", label: "Linked to incident", predicate: (i) => Boolean(i.incident_id) },
      { value: "standalone", label: "Standalone", predicate: (i) => !i.incident_id },
    ]},
    { key: "part", label: "Part #", type: "text", placeholder: "Part #",
      predicate: (i, v) => (i.part_number || "").toLowerCase().includes(v.toLowerCase()) },
    { key: "date_chip", label: "Requested", type: "dateRange", field: "requested_at" },
  ], []);
  const searchText = useCallback((i) => [
    i.id, i.part_number, i.part_description, i.reason, i.incident_id, i.incident_title,
    i.requested_by_name, i.locked_by_name, i.released_by_name,
  ].filter(Boolean).join(" "), []);

  const active = useMemo(() => items.filter((x) => ["requested", "locked"].includes(x.status)), [items]);
  const history = useMemo(() => items.filter((x) => ["released", "cancelled"].includes(x.status)), [items]);

  const onActionDone = () => {
    setActionLock(null);
    refresh();
  };

  return (
    <div data-testid="stock-locks-workspace">
      <Header canAct={canAct} onNew={() => setNewOpen(true)} />

      {newOpen && (
        <NewStockLockModal
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); refresh(); }}
        />
      )}

      {actionLock && (
        <LockActionPanel
          lock={actionLock}
          onClose={() => setActionLock(null)}
          onDone={onActionDone}
        />
      )}

      <Section title="§1 · Active Locks" testId="stock-locks-active-section">
        {active.length === 0 ? (
          <Empty text={loading ? "Loading…" : "No active stock locks."} testId="stock-locks-active-empty" />
        ) : (
          <UniversalFilter
            storageKey="stock_locks_active"
            items={active}
            getSearchText={searchText}
            chips={chips}
            placeholder="Search id, part #, reason, requester…"
            testIdPrefix="stock-locks-uf"
            emptyMessage="No locks match the current filter."
          >
            {({ filtered }) => (
              <Table
                rows={filtered}
                cols={["Lock ID", "Part / reason", "Status", "Requested", "Locked items", ""]}
                widths={[130, "flex-1", 110, 130, 130, 150]}
                testId="stock-locks-active-table"
                rowRenderer={(lk) => (
                  <LockRow
                    key={lk.id}
                    lock={lk}
                    focus={focusId === lk.id}
                    canAct={canAct}
                    onAction={() => setActionLock(lk)}
                  />
                )}
              />
            )}
          </UniversalFilter>
        )}
      </Section>

      <Section title="§2 · History" testId="stock-locks-history-section">
        {history.length === 0 ? (
          <Empty text={loading ? "Loading…" : "No released or cancelled locks yet."} testId="stock-locks-history-empty" />
        ) : (
          <UniversalFilter
            storageKey="stock_locks_history"
            items={history}
            getSearchText={searchText}
            chips={chips}
            placeholder="Search id, part #, reason…"
            testIdPrefix="stock-locks-history-uf"
            emptyMessage="No history rows match the current filter."
          >
            {({ filtered }) => (
              <Table
                rows={filtered}
                cols={["Lock ID", "Part / reason", "Status", "Closed", "Items", ""]}
                widths={[130, "flex-1", 110, 130, 130, 150]}
                testId="stock-locks-history-table"
                rowRenderer={(lk) => (
                  <LockRow key={lk.id} lock={lk} focus={focusId === lk.id} canAct={canAct} onAction={() => setActionLock(lk)} />
                )}
              />
            )}
          </UniversalFilter>
        )}
      </Section>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="stock-locks-stats">
        <StatCard label="Requested" value={counts.requested ?? 0} color="#f3b14a" testId="stock-locks-stat-requested" />
        <StatCard label="Locked" value={counts.locked ?? 0} color="#ff8a89" testId="stock-locks-stat-locked" />
        <StatCard label="Released" value={counts.released ?? 0} color="#9aebc2" testId="stock-locks-stat-released" />
        <StatCard label="Total" value={counts.all ?? 0} color="#9ec3ec" testId="stock-locks-stat-total" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const Header = ({ canAct, onNew }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div>
      <h1
        style={{
          fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800,
          fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)",
          letterSpacing: "-0.5px", lineHeight: 1.05,
        }}
        data-testid="view-title"
      >
        Stock Locks
      </h1>
      <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
        Quarantine / blocked / hold — request a lock, record locations &amp; quantities, release when safe
      </div>
    </div>
    {canAct && (
      <div className="flex gap-2 self-start sm:self-auto">
        <button
          type="button" onClick={onNew}
          className="px-4 py-2 rounded-md"
          style={{
            background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif",
            fontWeight: 700, fontSize: 13, letterSpacing: "0.4px",
            cursor: "pointer", border: "none",
          }}
          data-testid="stock-locks-new-btn"
        >
          ＋ New Stock Lock
        </button>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const LockRow = ({ lock, focus, canAct, onAction }) => {
  const status = STATUS_BADGE[lock.status] || STATUS_BADGE.requested;
  const requested = lock.requested_at ? new Date(lock.requested_at).toLocaleString() : "—";
  const closed = lock.released_at || (lock.status === "cancelled" ? lock.updated_at : null);
  const closedStr = closed ? new Date(closed).toLocaleString() : "—";
  const totalQty = (lock.locked_items || []).reduce((s, it) => s + (it.quantity || 0), 0);
  const itemCount = (lock.locked_items || []).length;

  const widths = [130, "flex-1", 110, 130, 130, 150];

  const actionLabel =
    lock.status === "requested" ? "Execute lock →" :
    lock.status === "locked" ? "Release →" : null;

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5"
      style={{
        background: focus ? "rgba(24,95,165,0.10)" : "var(--bg-surface, #0d0f18)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        borderLeft: focus ? "2px solid #185FA5" : "2px solid transparent",
      }}
      data-testid={`stock-locks-row-${lock.id}`}
    >
      <div style={{ flex: `0 0 ${widths[0]}px`, minWidth: 0 }}>
        <span style={mono(11, "#9ec3ec")}>{lock.id}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text, #f0f2f8)" }}>
          {lock.part_number}
          {lock.part_description ? <span style={{ color: "#9198b0", fontWeight: 400 }}> — {lock.part_description}</span> : null}
          {lock.quantity_estimate ? (
            <span className="ml-2" style={{ background: "rgba(78,208,132,0.10)", color: "#9aebc2", padding: "1px 6px", borderRadius: 3, fontFamily: "DM Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.4px" }} data-testid={`stock-locks-row-qty-${lock.id}`}>
              EST {lock.quantity_estimate} PCS
            </span>
          ) : null}
        </div>
        <div style={mono(9, "#9198b0")} className="truncate">
          {lock.reason}
          {lock.incident_id ? ` · ${lock.incident_id}` : " · standalone"}
          {lock.release_reminder_at ? (
            <span className="ml-2" style={{ color: "#f3b14a" }} data-testid={`stock-locks-row-deadline-${lock.id}`}>
              · ⏰ {new Date(lock.release_reminder_at).toLocaleString()}
            </span>
          ) : null}
        </div>
      </div>
      <div style={{ flex: `0 0 ${widths[2]}px` }}>
        <span style={{ background: status.bg, color: status.fg, ...mono(9, undefined, undefined, "0.5px"), padding: "2px 7px", borderRadius: 3 }}>
          {status.label}
        </span>
      </div>
      <div style={{ flex: `0 0 ${widths[3]}px`, minWidth: 0 }}>
        <span style={mono(10, "#c5cad8")}>{lock.status === "released" || lock.status === "cancelled" ? closedStr : requested}</span>
        <div style={mono(9, "#9198b0")} className="truncate">
          {lock.status === "released" ? `by ${lock.released_by_name || "—"}` :
           lock.status === "cancelled" ? "cancelled" :
           lock.status === "locked" ? `by ${lock.locked_by_name || "—"}` : `by ${lock.requested_by_name || "—"}`}
        </div>
      </div>
      <div style={{ flex: `0 0 ${widths[4]}px`, minWidth: 0 }}>
        {itemCount > 0 ? (
          <div>
            <span style={mono(11, "#9aebc2", 700)}>{totalQty.toLocaleString()} pcs</span>
            <div style={mono(9, "#9198b0")}>{itemCount} location{itemCount === 1 ? "" : "s"}</div>
          </div>
        ) : <span style={mono(10, "#6b7388")}>—</span>}
      </div>
      <div style={{ flex: `0 0 ${widths[5]}px` }}>
        {actionLabel && canAct ? (
          <button
            type="button"
            onClick={onAction}
            className="px-2.5 py-1 rounded"
            style={{
              background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.55)",
              color: "#9ec3ec", ...mono(10, undefined, undefined, "0.4px"),
              cursor: "pointer", whiteSpace: "nowrap",
            }}
            data-testid={`stock-locks-action-${lock.id}`}
          >
            {actionLabel}
          </button>
        ) : (
          <div style={mono(9, "#6b7388")}>—</div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Section = ({ title, children, testId }) => (
  <div className="mb-8" data-testid={testId}>
    <h2 className="mb-3" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)", letterSpacing: "-0.2px" }}>{title}</h2>
    {children}
  </div>
);

const StatCard = ({ label, value, color, testId }) => (
  <div
    className="rounded-md p-3"
    style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.12)" }}
    data-testid={testId}
  >
    <div style={mono(9, "#9198b0", undefined, "1.2px")} className="uppercase">{label}</div>
    <div className="mt-2" style={{ ...mono(22, color, 500), lineHeight: 1 }}>{value}</div>
  </div>
);

const Empty = ({ text, testId }) => (
  <div
    className="rounded-md p-6 text-center"
    style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", ...mono(11, "var(--text-dim, #9198b0)") }}
    data-testid={testId}
  >
    {text}
  </div>
);

const Table = ({ rows, cols, widths, testId, rowRenderer }) => (
  <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid={testId}>
    <div className="flex items-center gap-3 px-3 py-2" style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      {cols.map((c, i) => (
        <div key={i} style={{
          flex: widths[i] === "flex-1" ? 1 : `0 0 ${widths[i]}px`,
          width: widths[i] === "flex-1" ? undefined : widths[i],
          ...mono(9, "#9198b0", undefined, "1.2px"), textTransform: "uppercase",
        }}>{c}</div>
      ))}
    </div>
    {rows.map(rowRenderer)}
  </div>
);

// Re-export for the sidebar to use
export { LOCK_TYPE_COLOR };
